/* eslint-disable @typescript-eslint/camelcase */
import {AreaGroupService} from "../../area-group/area-group.service";
import {Component, OnDestroy} from "@angular/core";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowService} from "../workflow.service";
import {ProjectService} from "../../project/project.service";
import {RouteMapping} from "../../misc/route-helper";
import {WorkflowStep} from "../workflow-step";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {AreaGroupModel, areaGroupModelFactory} from "../../area-group/area-group.model";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {cloneDeep} from "lodash";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {GroupModel} from "../../area-group/group.model";
import {AreaModel} from "../../area-group/area.model";
import {SimpleHelpDialogComponent} from "../../help-dialog/simple-help-dialog.component";
import {ImportITSMGroupsDialogComponent} from "./import-itsm-groups-dialog.component";
import {AppSettingsService} from "../../app-settings/app-settings.service";
import {Subscription} from "rxjs";
import {Query} from "../../graphql/query-constant";
import { HasuraGraphqlService } from "../../graphql/hasura-graphql-service";

const uuid = require("uuid");

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

function tokenizeString(str) {
  if (!str) return [];
  return str.match(/(?:[^\\s"]+|"[^"]*")+/g);
}

function stripQuotes(str) {
  if (!str || !str.trim()) return "";
  const result = str.match(/^"(.*)"$/);
  if (result && result.length > 1) return result[1];
  return str;
}

function cleanParam(param = "") {
  const clean = ((typeof param) !== "string") ? `${param}` : param;
  return clean.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
}

@Component({
  templateUrl: "./project-area-group.template.pug",
  providers: [
    AreaGroupService,
    ConfirmDialogService,
    DialogService,
    ProjectService,
    HasuraGraphqlService,
  ],
})
export class ProjectAreaGroupComponent extends WorkflowStep implements OnDestroy {
  error: Error;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  searchText: string = "";
  itsmAreas: AreaModel[];
  itsmGroups: GroupModel[];

  projectId: string;
  groups: AreaGroupModel[] = [];
  sendGroups: AreaGroupModel[] = [];
  filteredGroups: AreaGroupModel[] = [];
  displayedGroups: AreaGroupModel[] = [];
  originalGroups: {[key: string]: AreaGroupModel} = {};

  itsmGroupsImportableCount: number = 0;
  itsmAreasLookup: {[key: string]: AreaModel} = {};
  itsmGroupsLookup: {[key: string]: GroupModel} = {};
  areaNameCapitalization: {[key: string]: string} = {};
  anyDuplicate: boolean = false;
  anyGroupDuplicate: boolean = false;
  anyITSMDuplicate: boolean = false
  anyGroupAreaNameRequiredErrors: boolean = false;

  private settingsSubscription: Subscription;
  private enableEmeaPMKitAutoOrdering: boolean = false;

  isEmea: boolean;
  projectHasCustomer: boolean = false;
  warningMessage: string = "";

  paramsMap: RouteMapping[] = [
    {fieldName: "projectId", paramName: "projectId"} as RouteMapping,
  ];

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    private areaGroupService: AreaGroupService,
    public workflowService: WorkflowService,
    private confirmDialogService: ConfirmDialogService,
    private dialogService: DialogService,
    private appSettingsService: AppSettingsService,
    private projectService: ProjectService,
    private hasuraGqlService: HasuraGraphqlService,
  ) {
    super();
    this.saveOnContinue = true;
  }

  private updateWarningMessage(): void {
    const customerRequiredMessage = this.projectHasCustomer ?
      null :
      `A ITSM Customer is required for this Step. Select Target Customer First.`;
    this.warningMessage = [
      customerRequiredMessage,
      this.anyGroupAreaNameRequiredErrors ? "Area and group name are required." : null,
      this.groups.some(group => group.areaDisabled || group.groupDisabled) ?
        "One or more Areas or Groups has been disabled in ITSM. Please remove or rename disabled group before saving." :
        null, // Assuming that groups/areas can be disabled in ITSM
      this.anyDuplicate || this.anyGroupDuplicate ? "Duplicate groups exist. Please remove or rename duplicate before saving." : null,
      this.anyITSMDuplicate ? "One or more groups already exist in ITSM. Please import from ITSM or rename duplicate before saving." : null,
    ].filter(it => it).join("  ");
  }

  private recalculateImportableTotal() {
    this.itsmGroupsImportableCount = this.itsmGroups.reduce(
      (acc, g) => ((!g.addedToProject && !g.areaDisabled && !g.groupDisabled) ? acc + 1 : acc),
      0,
    );
  }

  @handleErrors()
  async loadData(): Promise<void> {
    const {
      groups,
      itsmGroups,
      itsmAreas,
      projectHasCustomer,
    } = await this.areaGroupService.getGroupsByProjectId(this.projectId);
    this.itsmGroups = itsmGroups;
    this.itsmAreas = itsmAreas;

    this.itsmGroupsLookup = itsmGroups.reduce(
      (acc, group) => {
        if (group && group.areaName && group.groupName) {
          acc[group.key] = group;
        }
        return acc;
      },
      {},
    );

    this.groups = groups;

    this.itsmGroups.forEach((itsmGroup) => {
      this.groups.filter((_group) => {
        if (_group.areaName === itsmGroup.areaName && _group.groupName === itsmGroup.groupName &&
          _group.areaITSMId !== itsmGroup.areaITSMId) {
          _group.areaITSMId = itsmGroup.areaITSMId;
          _group.groupITSMId = itsmGroup.groupITSMId;
        }
      });
    });
    this.checkDuplicates();

    this.originalGroups = groups.reduce(
      (acc, group) => {
        acc[group.localId] = cloneDeep(group);
        return acc;
      },
      {},
    );

    this.areaNameCapitalization = groups.reduce( // Get a lookup table for the capitalization of an area
      (acc, group) => {
        const areaName = (group.areaName || "").toLowerCase();
        acc[areaName] = this.itsmAreasLookup[areaName] || group.areaName;
        return acc;
      },
      {},
    );

    this.recalculateImportableTotal();
    this.projectHasCustomer = projectHasCustomer;
    this.updateWarningMessage();
    this.updateDisplay();
  }

  private updateDisplay() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.filteredGroups = this.groups.filter((group) => {
      if (this.searchText) {
        const values = [
          group.id,
          group.areaName,
          group.groupName,
        ];

        const searchFailures = tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => stripQuotes(v)) // strip quotes on ends if present
          .map(v => cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayedGroups = this.filteredGroups.slice(start, end);

    this.recalculateMaxPages();
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filteredGroups.length / (+this.pageSize));
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        break;
    }
    this.updateDisplay();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.settingsSubscription && !this.settingsSubscription.closed) {
      this.settingsSubscription.unsubscribe();
    }
  }

  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.loading = true;
    this.settingsSubscription = this.appSettingsService.settings.subscribe((settings) => {
      this.enableEmeaPMKitAutoOrdering = settings["enableEmeaPMKitAutoOrdering"];
    });
    this.projectId = workflowTargetInfo.projectId;

    if (this.workflowService.workflowConfig.geoRegionId === "EMEA") this.isEmea = true;

    await this.loadData();

    this.loading = false;
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["projects"], {queryParams, skipLocationChange});
  }

  addAreaGroup(): void {
    const areaGroup = areaGroupModelFactory();

    this.cleanScreen();

    if (!this.groups) this.groups = [areaGroup];
    else this.groups.unshift(areaGroup);

    this.updateDisplay();
  }

  checkDuplicates() {
    let anyDuplicate = false;
    let anyGroupAreaRequired = false;
    const testBucket = {};
    const duplicates = {};

    this.groups.forEach(({key}) => {
      if (testBucket[key]) {
        anyDuplicate = true;
        duplicates[key] = true;
      } else {
        testBucket[key] = true;
      }
    });

    this.groups.forEach((group) => {
      group.groupNameRequired = !!(group.areaName && !group.groupName);
      group.areaNameRequired = !!(!group.areaName && group.groupName);

      anyGroupAreaRequired = anyGroupAreaRequired || group.groupNameRequired || group.areaNameRequired;

      group.duplicate = !!duplicates[group.key];
    });

    this.anyGroupAreaNameRequiredErrors = anyGroupAreaRequired;
    this.anyDuplicate = anyDuplicate;
  }

  checkDuplicateGroupName(group) {
    let isGroupDuplicate = false;
    this.anyGroupDuplicate = false;
    this.groups.forEach((g) => {
      if (g.groupName === group.groupName && g !== group) {
        isGroupDuplicate = true;
      }
    });
    if (isGroupDuplicate) {
      const updatedColumn = this.groups.find((g) => g === group);
      if (updatedColumn) {
        updatedColumn.duplicateGroup = true;
      }
    } else {
      const updatedColumn = this.groups.find((g) => g === group);
      if (updatedColumn) {
        updatedColumn.duplicateGroup = false;
      }
    }

    this.groups.forEach((g) => {
      if (g.duplicateGroup) {
        this.anyGroupDuplicate = true;
      }
    });
  }

  private areaNameChangeHelper(group) {
    const canonicalAreaName = (group.areaName || "").toLowerCase();
    if (canonicalAreaName) {
      // The user may have altered the capitalization of an area in which case the change needs to propagate to
      // all other groups with the same area since we know at this point this isn't an area in K2
      const areaCapitalization = this.areaNameCapitalization[canonicalAreaName];
      if (areaCapitalization) {
        if (areaCapitalization !== group.areaName) {
          this.areaNameCapitalization[canonicalAreaName] = group.areaName; // Record the change
          // Update any of the same areas capitalized differently
          // (we don't have to change derived data since these are all equivalent)
          this.groups.forEach((it) => {
            if ((it.areaName || "").toLowerCase() === canonicalAreaName) it.areaName = group.areaName;
          });
        }
      } else { // This is a new area, so update the lookup map
        this.areaNameCapitalization[canonicalAreaName] = group.areaName;
      }
    }
  }

  @handleErrors()
  async changed(group: AreaGroupModel) {
    group.clean();
    this.checkDuplicates();
    this.checkDuplicateGroupName(group);
    const groupNames: string[] = [];

    this.itsmGroups.filter((_ag) => {
      if (_ag.areaName === group.areaName) {
        group.existsInITSM = true;
        group.areaITSMId = _ag.areaITSMId;

        if (_ag.groupName === group.groupName) {
          group.groupITSMId = _ag.groupITSMId;
        }
      }
      if (!_ag.addedToProject) {
        groupNames.push(_ag.groupName.toLowerCase());
      }
    });

    this.anyITSMDuplicate = false;
    if (groupNames.includes(group.canonicalGroupName)) {
      group.itsmDuplicate = true;
      this.anyITSMDuplicate = true;
    } else {
      group.itsmDuplicate = false;
    }

    // TODO it needs to check if there are any coincidence in ITSM so the area group can be imported from it

    // Update the area if there are any changes needed do standardization
    this.areaNameChangeHelper(group);

    this.recalculateImportableTotal();
    this.updateWarningMessage();
  }

  @toggleLoading()
  @handleErrors()
  async autoSave(): Promise<boolean> {
    this.loading = true;
    this.cleanScreen();

    if (!this.warningMessage) {
      this.sendGroups = this.groups.filter(group =>
        (!this.itsmGroups.some(itsmGroup =>
          (itsmGroup.areaName === group.areaName && itsmGroup.groupName === group.groupName))
        ) &&
        (group.areaName.length > 0 && group.groupName.length > 0)
      );

      const areaMap = {};
      this.sendGroups.forEach((groups) => {
        // If area has no ID from ITSM use a generated id for all areas with same name
        if (!groups.areaITSMId) {
          if (!areaMap[groups.canonicalAreaName]) {
            areaMap[groups.canonicalAreaName] = uuid.v1().replace(/-/g, "");
          }
          groups.areaITSMId = areaMap[groups.canonicalAreaName];
        }
        groups.areaITSMId = groups.areaITSMId.replace(/-/g, "");
        groups.groupITSMId = groups.groupITSMId ? groups.groupITSMId.replace(/-/g, "") : uuid.v1().replace(/-/g, "");
      });

      if (this.sendGroups.length > 0) {
        await this.areaGroupService.sendAreaGroupsToITSM(this.projectId, this.sendGroups);
      }

      if (this.groups && this.groups.length > 0) {
        const insertInput = [];
        this.groups.map((group) => {
          if (group.areaName.length > 0 && group.groupName.length > 0) {
          const input = {
            area_id: group.areaITSMId.replace(/-/g, ""),
            area_name: group.areaName,
            group_name: group.groupName,
            itsm_group_id: group.groupITSMId.replace(/-/g, ""),
            project_id: this.projectId,
            id: group.id ? group.id : uuid.v1(),
            area_disabled: group.areaDisabled,
            group_disabled: group.groupDisabled,
            do_usage_collection: group.doUsageCollection,
            do_toner_monitoring: group.doTonerMonitoring,
            do_toner_auto_order: group.doTonerAutoOrder,
            do_ink_monitoring: group.doInkMonitoring,
            do_ink_auto_order: group.doInkAutoOrder,
            do_print_head_monitoring: group.doPrintHeadMonitoring,
            do_print_head_auto_order: group.doPrintHeadAutoOrder,
            do_maintenance_monitoring: group.doMaintenanceMonitoring,
            do_maintenance_auto_order: group.doMaintenanceAutoOrder,
            do_fastener_monitoring: group.doFastenerMonitoring,
            do_fastener_auto_order: group.doFastenerAutoOrder,
            itsm_consumable_auto_fulfillment: group.itsmConsumableAutoFulfillment,
            itsm_long_life_consumable_auto_fulfillment: group.itsmLongLifeConsumableAutoFulfillment,
            created_at: group.createdAt || new Date(),
            updated_at: group.updatedAt || new Date(),
          };
          insertInput.push(input);
          }
        });
        Query.SAVE_AREA_GROUP.variables.input = insertInput;
        const params = {
          projectId: this.projectId,
          query: Query.SAVE_AREA_GROUP,
          uiAction: "SAVE-AREA-GROUP",
        };
        await this.hasuraGqlService.getResponseFromHasura(params);
      }
      return true;
    }
    this.loading = false;
    return false;
  }

  @handleErrors()
  async removeAreaGroup(group: AreaGroupModel): Promise<any> {
    this.cleanScreen();

    const index = this.groups.indexOf(group);

    if (index < 0) {
      throw new Error("Cannot remove this row.");
    }

    this.groups.splice(index, 1);

    const itsmGroup = this.itsmGroupsLookup[group.key];

    if (itsmGroup) {
      itsmGroup.addedToProject = false;
      this.recalculateImportableTotal();
    }

    const groupNames: string[] = [];
    for (const ig of this.itsmGroups) {
      if (!ig.addedToProject) {
        groupNames.push(ig.groupName.toLowerCase());
      }
    }
    this.anyITSMDuplicate = false;
    for (const g of this.groups) {
      if (groupNames.includes(g.canonicalGroupName)) {
        g.itsmDuplicate = true;
        this.anyITSMDuplicate = true;
      } else {
        g.itsmDuplicate = false;
      }
    }

    this.checkDuplicates();
    this.checkDuplicateGroupName(group);
    this.updateWarningMessage();
    this.updateDisplay();
  }

  addGroups(list) {
    list.forEach((ITSMGroup) => {
      const group = new AreaGroupModel(ITSMGroup);
      this.groups.push(group);
      ITSMGroup.addedToProject = true;
    });

    this.recalculateImportableTotal();
    this.updateWarningMessage();
    this.updateDisplay();
  }

  async addITSMGroup(): Promise<any> {
    const result = await ImportITSMGroupsDialogComponent.showImportITSMGroupsDialogModal(
      this.dialogService,
      {itsmGroups: this.itsmGroups.filter(g => !g.addedToProject)},
    );

    if (!result) return;

    this.addGroups(result);
  }

  private cleanScreen(): void {
    this.error = null;
  }

  async showAsmDisableReason(group, type): Promise<void> {
    const messages = [];
    let readableName = "Flag Disablement Reasons";
    let help = "No known restrictions.";

    if (type === "groupDisable") {
      readableName = "Group Edit Disablement Reason(s)";
      if (group.disableEditReasons) help = group.disableEditReasons;
    } else {
      if (type === "consumable") {
        if (!group.hasConsumableAddressUploader) {
          messages.push(
            "A Consumable Address must be specified at the building level in order for ASM to be turned on by TMCfor Ink, Toner, Fasteners or Longlife Consumables.",
          );
        }
      } else if (type === "maintenance") {
        if (!group.hasServiceAddressUploader) {
          messages.push(
            "A Service Address must be specified at the site level in order for ASM to be turned on for Maintenance Kits by TMC.",
          );
        }
      }
      if (!group.hasVerificationContactUploader) {
        messages.push("A Verification Notify Contact is required at the asset level for all assets within a group for ASM to be turned on by TMC.");
      }
      if (!group.hasExceptionContactUploader) {
        messages.push("An Exception Notify Contact is required at the asset level for all assets within a group for ASM to be turned on by TMC.");
      }
      help = messages.length ? messages.join("  ") : "No known restrictions.";
    }

    await SimpleHelpDialogComponent.show(this.dialogService, {readableName, help});
  }

  isConsumableConflict(group) {
    const hasConsumableAsmEnabled =
      group.doTonerAutoOrder ||
      group.doInkAutoOrder ||
      group.doPrintHeadMonitoring ||
      group.doFastenerAutoOrder;

    return hasConsumableAsmEnabled && (
      !group.hasConsumableAddressUploader ||
      !group.hasVerificationContactUploader ||
      !group.hasExceptionContactUploader);
  }

  isMaintenanceConflict(group) {
    const hasMaintenanceAsmEnabled = group.doMaintenanceAutoOrder;

    return hasMaintenanceAsmEnabled && (
      !group.hasServiceAddressUploader ||
      !group.hasVerificationContactUploader ||
      !group.hasExceptionContactUploader);
  }

  async offerAsmFix(group) {
    const result = await this.confirmDialogService.confirm(
      "Confirm",
      "Some ASM flags cannot be updated because of a missing contact or address. Disable ASM flags to allow the edit on save?",
    );
    if (result) {
      if (this.isConsumableConflict(group)) {
        group.doTonerMonitoring = false;
        group.doInkAutoOrder = false;
        group.doPrintHeadMonitoring = false;
        group.doFastenerAutoOrder = false;
        if (this.isEmea) {
          group.doTonerMonitoring = false;
          group.doInkMonitoring = false;
        }
      }
      if (this.isMaintenanceConflict(group)) {
        group.doMaintenanceAutoOrder = false;
      }
    }
  }

  async change(group) {
    group.change();
    if (this.isConsumableConflict(group) || this.isMaintenanceConflict(group)) {
      await this.offerAsmFix(group);
    }
  }

  async itsmComsumableChanged() {
    // placeholder - currently there is no integrity checks because ITSM does not store flags
  }

  async itsmLonglifeChanged() {
    // placeholder - currently there is no integrity checks because ITSM does not store flags
  }

  async itsmMaintenanceChanged() {
    // placeholder - currently there is no integrity checks because ITSM does not store flags
  }
}
